import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import Button from "../../../components/Button";
import Title from "../../../components/Admin/Title";
import useIsAdmin from "../../../hooks/useIsAdmin";
import ErrorPage from "../../ErrorPage";
import { useUser } from "../../../components/UserContext";
import { useParams } from "react-router-dom";
import LoadingContent from "../../../components/LoadingContent";
import UploadRaceDiscord from "../../../components/Admin/SocialMedia/UploadRaceDiscord";
import UploadRaceX from "../../../components/Admin/SocialMedia/UploadRaceX";
import UploadRaceFacebook from "../../../components/Admin/SocialMedia/UploadRaceFacebook";
import UploadRaceInstagram from "../../../components/Admin/SocialMedia/UploadRaceInstagram";

const SocialMediaUploadGP = () => {
  const { access } = useIsAdmin();
  document.title = "LVF1 - Social Media Upload GP";
  let { grand_prix_event_id } = useParams();
  const { user } = useUser() as any;
  const token = window.localStorage.getItem("userLogged");
  const [logs, setLogs] = useState<string[]>([]);
  // Estados compartidos
  const [xEmbed, setXEmbed] = useState(``);
  const [instagramEmbed, setInstagramEmbed] = useState(``);
  const [facebookEmbed, setFacebookEmbed] = useState(``);
  const [discordTitle, setDiscordTitle] = useState(``);
  const [discordEmbed, setDiscordEmbed] = useState(``);

  const [data, setData] = useState<any>();
  const [validated, setValidated] = useState(false);

  // Estados de carga independientes y global
  const [isSubmittingAll, setIsSubmittingAll] = useState(false);
  const [loadingFB, setLoadingFB] = useState(false);
  const [loadingX, setLoadingX] = useState(false);
  const [loadingDiscord, setLoadingDiscord] = useState(false);
  const [loadingInstagram, setLoadingInstagram] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  const [hastaghs, setHastaghs] = useState([
    "#LVF1",
    "#SimRacing",
    "#Motorsport",
    "#Esports",
    "#RacingCommunity",
    "#SimRacer",
    "#OnlineRacing",
    "#CompetitiveGaming",
    "#EsportsRacing",
    "#MotorsportGaming",
  ]);

  const BASE_URL =
    process.env.NODE_ENV === "development"
      ? "http://localhost:5500"
      : "https://ligavirtualf1.es:5500";

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/grandprix/getSocialMediaUploadGPInfo`, {
        params: { grand_prix_event_id },
      })
      .then(({ data }) => {
        setHastaghs((prev) => [
          "#" + data.grand_prix.grand_prix.championship.name.split(" ")[0],
          "#" + data.grand_prix.circuit.name.replace(/\s+/g, ""),
          ...prev,
        ]);
        setData(data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }, [grand_prix_event_id, BASE_URL]);

  // Auxiliar para empaquetar FormData de manera idéntica
  const createFormData = (
    key: string,
    value: string,
    extraKey?: string,
    extraValue?: string,
    extraKey2?: string,
    extraValue2?: string,
  ) => {
    if (!token || !grand_prix_event_id) return null;
    const formData = new FormData();
    formData.append(key, value);
    if (extraKey && extraValue) formData.append(extraKey, extraValue);
    if (extraKey2 && extraValue2) formData.append(extraKey2, extraValue2);
    formData.append("grand_prix_event_id", grand_prix_event_id);
    formData.append("user", user);
    formData.append("token", JSON.parse(token).token);
    return formData;
  };

  // --- ENVIOS INDIVIDUALES ---
  const submitFacebook = async () => {
    if (!facebookEmbed.trim())
      return toast.error("El campo de Facebook está vacío");
    setLoadingFB(true);
    try {
      const body = createFormData("facebook_embed", facebookEmbed);
      await axios.post(
        `${BASE_URL}/api/admin/publishFacebookRaceResults`,
        body,
      );
      toast.success("Facebook post created successfully");
    } catch (err) {
      toast.error("Error posting to Facebook");
    } finally {
      setLoadingFB(false);
    }
  };

  const submitX = async () => {
    if (!xEmbed.trim()) return toast.error("El campo de X está vacío");
    setLoadingX(true);
    try {
      const body = createFormData("x_embed", xEmbed, "type", "imagegp"); // Ajusta la key si es distinta
      await axios.post(`${BASE_URL}/api/admin/publishXRaceResults`, body);
      toast.success("X post created successfully");
    } catch (err) {
      toast.error("Error posting to X");
    } finally {
      setLoadingX(false);
    }
  };

  const submitDiscord = async () => {
    if (!discordEmbed.trim())
      return toast.error("El campo de Discord está vacío");
    setLoadingDiscord(true);
    try {
      const body = createFormData(
        "discord_embed",
        discordEmbed,
        "discord_title",
        discordTitle,
        "type_championship",
        data.grand_prix.grand_prix.championship.type_championship,
      );

      await axios.post(`${BASE_URL}/api/admin/publishDiscordRaceResults`, body);
      toast.success("Discord message created successfully");
    } catch (err) {
      toast.error("Error posting to Discord");
    } finally {
      setLoadingDiscord(false);
    }
  };

  const submitInstagram = async () => {
    setLoadingInstagram(true);

    try {
      // 2. Creamos el FormData base (usando tu helper o de forma nativa)
      // Si tu ayudante 'createFormData' no soporta arrays de archivos, lo hacemos a mano:
      const body = createFormData("instagram_embed", instagramEmbed);

      if (body != null) {
        // Añadimos los datos que ya tenías
        body.append("gp_name", data.grand_prix.name);
        if (instagramEmbed) {
          body.append("instagram_embed", instagramEmbed); // Por si sigues queriendo mandar el texto/caption
        }

        // 3. Añadimos CADA imagen al FormData
        // Usamos el mismo nombre de clave (ej: "images"), el backend lo recibirá como un array
        selectedImages.forEach((image) => {
          body.append("images", image);
        });
      }
      // 4. Petición a TU backend (no a Facebook directamente desde React)
      await axios.post(
        `${BASE_URL}/api/admin/publishInstagramRaceResults`,
        body,
        {
          headers: {
            "Content-Type": "multipart/form-data", // Importante para enviar archivos
          },
        },
      );

      toast.success("¡Imágenes enviadas al backend con éxito!");
      setSelectedImages([]); // Limpiamos el estado al terminar
    } catch (err) {
      console.error(err);
      toast.error("Error al procesar las imágenes en el servidor");
    } finally {
      setLoadingInstagram(false);
    }
  };

  // --- ENVÍO DE TODO EN LOTE ---
  const handleSubmitAll = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      toast.error("Por favor, rellena todos los campos requeridos (*)");
      return;
    }

    setValidated(true);
    setIsSubmittingAll(true);
    setLogs([]); // Limpiamos logs anteriores si los hubiera

    // Helper interno para añadir líneas al log de forma limpia
    const addLog = (message: string) => {
      setLogs((prev) => [
        ...prev,
        `[${new Date().toLocaleTimeString()}] ${message}`,
      ]);
    };

    addLog("🚀 Iniciando publicación simultánea en todas las redes...");

    // Preparamos FormDatas
    const instagramBatchData = createFormData(
      "instagram_embed",
      instagramEmbed,
    );
    if (instagramBatchData)
      instagramBatchData.append("gp_name", data.grand_prix.name);

    const discordBatchData = createFormData(
      "discord_embed",
      discordEmbed,
      "discord_title",
      discordTitle,
      "type_championship",
      data.grand_prix.grand_prix.championship.type_championship,
    );

    // Envolvemos cada petición en su propio mini-ciclo para capturar el progreso real
    const facebookPromise = axios
      .post(
        `${BASE_URL}/api/admin/publishFacebookRaceResults`,
        createFormData("facebook_embed", facebookEmbed),
      )
      .then(() => addLog("✅ Facebook: Posted successfully."))
      .catch(() => addLog("❌ Facebook: Error in the post."));

    const xPromise = axios
      .post(
        `${BASE_URL}/api/admin/publishXRaceResults`,
        createFormData("x_embed", xEmbed, "type", "imagegp"),
      )
      .then(() => addLog("✅ X (Twitter): Posted successfully."))
      .catch(() => addLog("❌ X (Twitter): Error in the post."));

    const discordPromise = axios
      .post(`${BASE_URL}/api/admin/publishDiscordRaceResults`, discordBatchData)
      .then(() => addLog("✅ Discord: Message sent successfully."))
      .catch(() => addLog("❌ Discord: Shipping error."));

    const instagramPromise = axios
      .post(
        `${BASE_URL}/api/admin/publishInstagramRaceResults`,
        instagramBatchData,
      )
      .then(() => addLog("✅ Instagram: Posted successfully."))
      .catch(() => addLog("❌ Instagram: Error en la publicación."));

    try {
      // Esperamos a que terminen todas sus flujos individuales
      await Promise.all([
        facebookPromise,
        xPromise,
        discordPromise,
        instagramPromise,
      ]);
      addLog("🏁 Batch processing completed.");
      toast.success("Batch operation completed! Check the log for details.");
      setValidated(false);
    } catch (error) {
      toast.error("A critical error occurred while processing the batch.");
    } finally {
      setIsSubmittingAll(false);
    }
  };

  if (!access || grand_prix_event_id == null) return <ErrorPage />;
  if (data == null) return <LoadingContent />;

  return (
    <div className="full-height padding-md app-container flex-center-top page-transition">
      <div className="form-container">
        <Title text="Upload Social Media GP Results" />
        <small className="mandatory-fields">(*) Required fields</small>

        <Form noValidate validated={validated} onSubmit={handleSubmitAll}>
          <UploadRaceDiscord
            data={data}
            discordTitle={discordTitle}
            setDiscordTitle={setDiscordTitle}
            discordEmbed={discordEmbed}
            setDiscordEmbed={setDiscordEmbed}
            onIndividualSubmit={submitDiscord}
            uploading={loadingDiscord}
          />

          <UploadRaceX
            data={data}
            xEmbed={xEmbed}
            setXEmbed={setXEmbed}
            onIndividualSubmit={submitX}
            uploading={loadingX}
            hastaghs={hastaghs}
          />

          <UploadRaceFacebook
            data={data}
            facebookEmbed={facebookEmbed}
            setFacebookEmbed={setFacebookEmbed}
            onIndividualSubmit={submitFacebook}
            uploading={loadingFB}
          />

          <UploadRaceInstagram
            data={data}
            instagramEmbed={instagramEmbed}
            setInstagramEmbed={setInstagramEmbed}
            onIndividualSubmit={submitInstagram}
            uploading={loadingInstagram}
            selectedImages={selectedImages}
            setSelectedImages={setSelectedImages}
            hastaghs={hastaghs}
          />
          {/* Consola de logs en vivo */}
          {logs.length > 0 && (
            <div id="deployment-log" className="mt-4 p-3 rounded text-start">
              <div>⚙️ Live Deployment Log:</div>
              {logs.map((log, index) => (
                <div key={index} className="mb-1">
                  {log}
                </div>
              ))}
            </div>
          )}
          {/* Botón Maestro Global */}
          <div className="text-center mt-4 mb-5">
            <Button
              text={
                isSubmittingAll
                  ? "Posting everything..."
                  : "Share on all social media platforms"
              }
              type="submit"
              disabled={isSubmittingAll}
              icon={isSubmittingAll ? faSpinner : faPaperPlane}
            />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default SocialMediaUploadGP;
